import { Box, Button, Card, Flex, Group, Loader, Text, Transition, UnstyledButton } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useEffect, useState } from "react";

import type { SpellCheckResponse, SpellCheckStatus as SpellCheckStatusType } from "@/sdk/agent/chat/types";

import { withPolymorphicClickAnalytics } from "@/analytics";
import { popUpTransition } from "@/components/chat/custom-transitions";
import { env } from "@/config";
import { classNames } from "@/shared-utils";

import { SPELLCHECK_CONFIG } from "./config";
import { highlightText } from "./highlightText";
import classes from "./spellCheck.module.css";

const AnalyticsButton = withPolymorphicClickAnalytics(Button<"button">, "Click");
const AnalyticsUnstyledButton = withPolymorphicClickAnalytics(UnstyledButton<"button">, "Click");

/**
 * Human-readable description of the issues fixed.
 * For now, only returns a generic count. In the future, could parse snippets for specific issue types.
 * TODO: Ask gaia to return specific issue types (e.g., "All caps text", "Spelling error") in the highlightSnippets array
 */
function getIssueDescription(highlightSnippets: string[]): string {
    const count = highlightSnippets?.length ?? 0;
    return count === 1 ? "1 issue fixed" : `${count} issues fixed`;
}

interface SpellCheckStatusProps {
    /** Current status of the spellcheck */
    status: SpellCheckStatusType;
    /** Result from spellcheck API */
    result?: SpellCheckResponse;
    /** Error message from spellcheck failure (shown in dev mode only) */
    errorMessage?: string;
    /** How long to show success message (ms) */
    successDisplayDurationMs?: number;
    /** How long to show error message (ms) */
    errorDisplayDurationMs?: number;
    /** Whether agent can bypass spellcheck and send original message */
    allowSendOriginal?: boolean;
    /** Called when user clicks Cancel on suggestions */
    onCancel: () => void;
    /** Called when user clicks Send with changes */
    onSendWithChanges: () => void;
    /** Called when user clicks Send original (only when allowSendOriginal is true) */
    onSendOriginal?: () => void;
    /** Called when success/error status should be dismissed */
    onDismiss: () => void;
}

/**
 * Displays the spellcheck status and suggestions UI.
 *
 * States:
 * - idle: Nothing shown
 * - checking: Loading spinner with "Checking for grammar and spelling..."
 * - success: Green dot with "No issues found – Message sent!" (auto-dismisses)
 * - error: Warning icon with "Couldn't check the grammar and spelling — Message sent!" (auto-dismisses)
 * - suggestions: Shows corrected text with highlights and action buttons
 */
export function SpellCheckStatus({
    status,
    result,
    errorMessage,
    successDisplayDurationMs = SPELLCHECK_CONFIG.successDisplayMs,
    errorDisplayDurationMs = SPELLCHECK_CONFIG.errorDisplayMs,
    allowSendOriginal = false,
    onCancel,
    onSendWithChanges,
    onSendOriginal,
    onDismiss,
}: SpellCheckStatusProps) {
    const [isVisible, setIsVisible] = useState(true);

    // Delay mounted so Transition runs enter animation (it skips animation when mounting with mounted=true)
    const [suggestionsMounted, setSuggestionsMounted] = useState(false);
    useEffect(() => {
        if (status === "suggestions" && result) {
            setSuggestionsMounted(false);
            const id = requestAnimationFrame(() => setSuggestionsMounted(true));
            return () => cancelAnimationFrame(id);
        }
        setSuggestionsMounted(false);
    }, [status, result]);

    // Auto-dismiss success and error states after duration
    useEffect(() => {
        if (status === "success" || status === "error") {
            const duration = status === "success" ? successDisplayDurationMs : errorDisplayDurationMs;
            const timer = setTimeout(() => {
                setIsVisible(false);
                onDismiss();
            }, duration);
            return () => clearTimeout(timer);
        }
        // Reset visibility for other states
        setIsVisible(true);
    }, [status, successDisplayDurationMs, errorDisplayDurationMs, onDismiss]);

    const showContent = status !== "idle" && isVisible;
    const showSuggestions = status === "suggestions" && !!result && showContent;
    // Suggestions need a rAF-delayed mount so Mantine runs the enter animation
    const suggestionsMountedDelayed = showSuggestions && suggestionsMounted;

    // When corrections are about to appear, the loading indicator should exit instantly so they don't overlap.
    // For success/error there is nothing replacing it, so the slow fade looks intentional.
    const checkingExitDuration = status === "suggestions" ? 0 : 350;

    const isDevelopment = env.mode === "development";
    const issueDescription = result ? getIssueDescription(result.highlightSnippets) : "";

    // Each Transition uses keepMounted so the exit animation plays before the element leaves the DOM.
    // All states are always present in the tree; only one is visible at a time via mounted.
    return (
        <>
            {/* Checking state */}
            <Transition
                mounted={showContent && status === "checking"}
                transition="pop"
                duration={200}
                exitDuration={checkingExitDuration}
                timingFunction="ease"
                keepMounted
            >
                {(transitionStyles) => (
                    <Box w="100%" px="md" py="xs" style={transitionStyles}>
                        <Flex align="center" justify="center" gap="xs" px="md" py="xs">
                            <Loader size="xs" mr="xs" />
                            <Text span size="sm">
                                Checking for grammar and spelling...
                            </Text>
                        </Flex>
                    </Box>
                )}
            </Transition>

            {/* Success state */}
            <Transition
                mounted={showContent && status === "success"}
                transition="pop"
                duration={200}
                exitDuration={350}
                timingFunction="ease"
                keepMounted
            >
                {(transitionStyles) => (
                    <Box w="100%" px="md" py="xs" style={transitionStyles}>
                        <Flex align="center" justify="center" gap="xs" px="md" py="xs">
                            <Box className={classNames(classes.statusIcon, classes.statusIconSuccess)} />
                            <Text span size="sm">
                                No issues found – Message sent!
                            </Text>
                        </Flex>
                    </Box>
                )}
            </Transition>

            {/* Error state — TODO: tune duration separately from success; consider if warning color triggers tech aux escalations */}
            <Transition
                mounted={showContent && status === "error"}
                transition="pop"
                duration={200}
                exitDuration={350}
                timingFunction="ease"
                keepMounted
            >
                {(transitionStyles) => (
                    <Box w="100%" px="md" py="xs" style={transitionStyles}>
                        <Flex align="center" justify="center" gap="xs" px="md" py="xs" direction="column">
                            <Flex>
                                <IconAlertTriangle size={16} color="var(--mantine-color-yellow-6)" />
                                <Text span>Couldn&apos;t check the grammar and spelling — Message sent!</Text>
                            </Flex>
                            {isDevelopment && errorMessage && (
                                <Text span size="xs" c="dimmed" ff="monospace">
                                    Reason: {errorMessage}
                                </Text>
                            )}
                        </Flex>
                    </Box>
                )}
            </Transition>

            {/* Suggestions state – more pronounced pop for the larger card */}
            <Transition
                mounted={suggestionsMountedDelayed}
                transition={popUpTransition}
                duration={280}
                exitDuration={350}
                timingFunction="ease-out"
                keepMounted
            >
                {(transitionStyles) => (
                    <Card className={classes.suggestionsCard} radius="xl" style={transitionStyles}>
                        <Flex align="center" gap="xs" c="red.4" className={classes.issueHeader}>
                            <Box className={classNames(classes.statusIcon, classes.statusIconIssue)} />
                            <Text span size="sm">
                                {issueDescription}
                            </Text>
                        </Flex>

                        <Box className={classes.correctedTextArea}>
                            <Text className={classes.correctedText}>
                                {result &&
                                    highlightText(result.rewrittenText, result.highlightSnippets ?? [], {
                                        highlightClassName: classes.highlighted,
                                    })}
                            </Text>
                            <Group justify="flex-end" gap="md" className={classes.actionsInside}>
                                <AnalyticsUnstyledButton
                                    id="spellcheck-cancel"
                                    analytics={{ actionId: "ChatSpellCheck_Cancel" }}
                                    className={classes.cancelButton}
                                    onClick={onCancel}
                                >
                                    Cancel
                                </AnalyticsUnstyledButton>
                                {allowSendOriginal && onSendOriginal && (
                                    <AnalyticsUnstyledButton
                                        id="spellcheck-send-original"
                                        analytics={{ actionId: "ChatSpellCheck_SendOriginal" }}
                                        className={classes.cancelButton}
                                        onClick={onSendOriginal}
                                    >
                                        Send original
                                    </AnalyticsUnstyledButton>
                                )}
                                <AnalyticsButton
                                    id="spellcheck-send-with-changes"
                                    analytics={{ actionId: "ChatSpellCheck_SendWithChanges" }}
                                    className={classes.sendWithChangesButton}
                                    size="sm"
                                    onClick={onSendWithChanges}
                                >
                                    Send with changes
                                </AnalyticsButton>
                            </Group>
                        </Box>
                    </Card>
                )}
            </Transition>
        </>
    );
}
